/**
 * aminos-nutrition-support-checkout.js
 *
 * Loaded ONLY by templates/page.nutrition-support.json (/pages/nutrition-support), from its
 * custom_liquid_ns block. Page-scoped fork of assets/aminos-adults-30-checkout.js,
 * itself forked from
 * assets/aminos-first-offer-checkout.js.
 *
 * THIS IS THE FIRST PAGE THAT ACTUALLY APPLIES A DISCOUNT CODE
 * On first-offer and fussy-eaters every buy box ships checkout_discount_code
 * blank — the whole reduction comes from the Loop selling plan, so the discount
 * block below is gated off and the /discount/<code> redirect never runs. Here it
 * does: the Adults plan is 25% ($52.50) and the page advertises 30% ($49.00), so
 * `save30adults` (a fixed $3.50 off, per item) carries the last $3.50. That
 * makes two things load-bearing that were previously dead weight:
 *   - the /discount/<code>?redirect=/checkout hand-off, because a code written
 *     only to the cart via /cart/update.js does NOT survive into checkout;
 *   - the `applicable: false` check, which is the only signal that Shopify
 *     refused the code (e.g. it is not enabled for Subscription purchase type).
 * If the displayed price and the charged price ever drift apart, check those two
 * first.
 *
 * 3-MONTH CARD AND ONE-TIME LINK (Oct 2026)
 * The 3-month card ([data-ns-three-month]) is priced by its Loop plan alone, so
 * it goes to a bare /checkout with every code cleared, never save30adults or
 * save35adults (both are per-item and would stack under the 40%). The one-time
 * text link ([data-ns-otp-checkout]) takes the same clear-and-add route.
 *
 * WHY AN OVERRIDE AND NOT AN EDIT TO THE SHARED CONTROLLER
 * assets/aminos-featured-product.js is shared by ~15 pages and pins itself to
 * the cart drawer (`const destination = 'drawer'`). Twelve templates already
 * carry `cta_destination: "checkout"`, so teaching that file to honour the
 * setting would flip all twelve at once. The landing pages are independent, so
 * this behaviour lives in a page-scoped file instead. The shared controller,
 * its section and its snippet are untouched.
 *
 * WHAT IT CHANGES
 *   1. The CTA goes straight to /checkout instead of opening the cart drawer,
 *      matching the button's own "Continue to checkout" label.
 *   2. The cart is emptied and rebuilt, so checkout contains exactly the offer
 *      and quantity shown on the button — nothing else. /cart/add.js APPENDS, so
 *      add -> browser back -> add again silently produced quantity 2, and two
 *      landers sharing one cart sent both products to checkout. Shopify's
 *      one-page checkout won't let a shopper edit the quantity of a subscription
 *      line, and these pages hide the theme header so there is no cart icon to
 *      fix it with — either way they could only abandon. That double-add is the
 *      whole reason the shared controller pinned itself to the drawer (see its
 *      2026-08-12 comment); replacing the cart is what makes skipping it safe.
 *      See resetCart() for the trade-off this accepts.
 *
 * HOW IT TAKES OVER
 * The listener below runs in the CAPTURE phase on document. The shared
 * controller listens in the BUBBLE phase on document, so capture always runs
 * first regardless of script load order, and stopPropagation() means the
 * shared handler never fires. Exactly one POST /cart/add.js per click.
 */
(function (global) {
  'use strict';

  // Single-bind guard, mirroring the shared controller. The script tag is
  // emitted once here, but a theme-editor section reload can re-run it.
  if (global.__aminosNutritionSupportCheckout) return;
  global.__aminosNutritionSupportCheckout = true;

  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    }).then(function (response) {
      if (response.ok) return response.json();
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (err) {
          throw new Error(err.description || url + ' failed: ' + response.status);
        });
    });
  }

  // Replace the cart outright rather than de-duplicating just this offer's lines.
  //
  // This is a single-offer landing page whose CTA goes straight to /checkout, and
  // it hides the theme header (see the template's custom_liquid) — there is no
  // cart icon and no nav, so a line left over from anywhere else is invisible to
  // the shopper until the checkout page. Worse, they often cannot fix it once
  // there: Shopify's one-page checkout will not let a shopper edit the quantity
  // of a SUBSCRIPTION line, so a stranded offer from another lander leaves
  // abandoning as the only way out.
  //
  // Clearing rather than de-duplicating this page's own variant is what stops
  // /pages/nutrition-support, /pages/first-offer and /pages/fussy-eaters — all three
  // selling a subscription of the same two products — checking out together when
  // a shopper visits more than one.
  //
  // TRADE-OFF, accepted deliberately: a cart the shopper built elsewhere on the
  // store is discarded when they hit Continue to checkout here. These pages are
  // cold-traffic landers for one offer, and quoting $X on the button while
  // charging $X plus someone else's cart is the worse failure.
  //
  // /cart/clear.js empties line items only; it does NOT drop a discount code
  // already applied to the session. On this page that matters, because unlike
  // the other landers this one does configure a code (save30adults).
  //
  // The sequence that would otherwise leak: choose Subscribe (code goes into the
  // session via the /discount/ redirect), hit browser back, switch to One-time,
  // check out again. This function clears the line items but not the session
  // code, and the one-time path deliberately navigates to a bare /checkout, so
  // nothing here removes it.
  //
  // What stops that being a real discount leak is the code's own config, not
  // this file: save30adults is restricted to Purchase type = Subscription, so
  // Shopify will not apply it to a one-time line even when the session is still
  // carrying it. If that restriction is ever lifted, a one-time order from this
  // page could take $3.50 off — re-check this comment first.
  function resetCart() {
    return post('/cart/clear.js', {});
  }

  async function checkout(form) {
    var variantId = Number(form.dataset.variantId);
    if (!variantId) {
      console.warn('[aminos-nutrition-support-checkout] Missing data-variant-id');
      return;
    }

    var planInput = form.querySelector('[data-aminos-fp-selling-plan]');
    var sellingPlanId = planInput && planInput.value ? Number(planInput.value) : null;

    var qtyField =
      form.querySelector('input[name="quantity"]:checked') || form.querySelector('input[name="quantity"]');
    var quantity = Math.max(1, parseInt((qtyField && qtyField.value) || '1', 10) || 1);

    // 3-month card (Oct 2026): three_month_qty units (3 bags) per adult on the
    // 3-month plan, so 2 adults = 6. Its price is the plan's own, so it never
    // takes a code: save30adults is $3.50 off PER ITEM and would take another
    // $10.50 off every three bags, and there is no 2-adult bonus on it.
    var adults = quantity;
    var picked = form.querySelector('[data-aminos-fp-radio]:checked');
    var threeMonth = !!(picked && picked.hasAttribute('data-ns-three-month'));
    if (threeMonth) {
      variantId = Number(picked.dataset.nsVariant) || variantId;
      quantity = adults * Math.max(1, parseInt(picked.dataset.nsQty, 10) || 1);
    }

    var btn = form.querySelector('[data-aminos-fp-submit]');
    var btnLabel = btn ? btn.querySelector('span') : null;

    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }
    if (btnLabel) {
      // Stash on the element, not in a local: the success path navigates away
      // without restoring, and the bfcache reset below needs the original label
      // long after this function has returned.
      if (btnLabel.dataset.originalLabel == null) btnLabel.dataset.originalLabel = btnLabel.textContent;
      // Not "Adding…": nothing visibly gets added any more, the shopper is sent
      // straight to checkout. Kept short because the CSS uppercases this and the
      // button is already near its width limit on a narrow phone.
      btnLabel.textContent = 'One moment…';
    }

    try {
      await resetCart();

      var payload = { id: variantId, quantity: quantity };
      if (sellingPlanId) payload.selling_plan = sellingPlanId;
      // Pricing switch (Oct 29): 2+ adults go on the flat bonus plans (monthly or 3-month), so no code is needed.
      var bundlePlanMin = parseInt(form.dataset.nsBundleMin, 10) || 0;
      var bundlePlan = ((threeMonth ? form.dataset.nsBundlePlanThree : form.dataset.nsBundlePlan) || '').trim();
      if (sellingPlanId && bundlePlan && bundlePlanMin && adults >= bundlePlanMin) payload.selling_plan = Number(bundlePlan);
      await post('/cart/add.js', payload);

      // This lander advertises a first-order price below the recurring
      // selling-plan allocation, so the code has to be on the cart for buy box
      // and checkout to agree. Clearing first is essential — Shopify persists
      // offer codes between landers. One-time purchases get no code.
      // Multi-adult bonus: at the bonus quantity the buy box shows the bonus
      // price, so send the bonus code instead (subscription only, like the
      // main code). The code must also carry a minimum quantity in Shopify
      // so it can't be used on a single unit.
      var configuredDiscount = (form.dataset.aminosFpDiscount || '').trim();
      var bundleCode = (form.dataset.nsBundleCode || '').trim();
      var bundleMin = parseInt(form.dataset.nsBundleMin, 10) || 0;
      if (bundleCode && bundleMin && adults >= bundleMin) configuredDiscount = bundleCode;
      var discount = sellingPlanId && !threeMonth ? configuredDiscount : '';
      if (configuredDiscount || threeMonth) {
        await post('/cart/update.js', { discount: '' });
        if (discount) {
          var updated = await post('/cart/update.js', { discount: discount });

          // Shopify accepts this request even when the code cannot be applied —
          // it reports that per code via `applicable` (same contract the theme's
          // own discount form checks at assets/cart.js:487). Firing it blind
          // meant a shopper promised 40% off could land on a full-price
          // checkout with nothing logged. Usual cause: the code's Purchase type
          // excludes Subscriptions, so it is rejected on a selling-plan line.
          var rejected = (updated.discount_codes || []).some(function (code) {
            return code.code === discount && code.applicable === false;
          });
          if (rejected) {
            console.error(
              '[aminos-nutrition-support-checkout] Shopify rejected discount "' +
                discount +
                '" (applicable: false). Check the code is enabled for Subscription ' +
                'purchase type and matches this product.'
            );
          }
        }
      }

      // Route through Shopify's discount link rather than straight to /checkout.
      //
      // The `discount` param on /cart/update.js above is an undocumented feature:
      // it puts the code on the CART object (cart.total_discount does show the
      // $14, verified) but checkout resolves discounts from the session, not from
      // cart.discount_codes — so the code was silently dropped and the shopper
      // landed on $56 instead of $42. /discount/{code}?redirect= is the supported
      // mechanism: it sets the code server-side, then forwards to the target.
      //
      // The cart-level application is kept because it is what makes the cart and
      // drawer display the discounted total if the shopper ever opens them.
      //
      // Stash what they picked first: that redirect is what makes browser-Back a
      // fresh load, so this is the last moment the selection exists. See
      // readReturnState below.
      // qty is the adults picker value, not the unit count (3-month sends 3 per adult).
      writeReturnState({ t: Date.now(), qty: adults, plan: sellingPlanId || '' });

      global.location.href = discount
        ? '/discount/' + encodeURIComponent(discount) + '?redirect=' + encodeURIComponent('/checkout')
        : '/checkout';
      return;
    } catch (err) {
      // Deliberately no drawer fallback: a failed add must not look like a
      // success. Restore the button so the shopper can retry.
      console.error('[aminos-nutrition-support-checkout] Checkout failed', err);
    }

    resetSubmitButton(btn);
  }

  // One-time as a text link under the CTA ([data-ns-otp-checkout], Oct 2026).
  // Its href is a cart permalink so it still works without JS; here it gets
  // the same hand-off as the main button: clear the cart, add one bag with no
  // plan, drop any code the session picked up from an earlier subscription
  // attempt, then a bare /checkout.
  async function checkoutOneTime(link) {
    var variantId = Number(link.dataset.variantId);
    if (!variantId || link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');
    if (link.dataset.originalLabel == null) link.dataset.originalLabel = link.textContent;
    link.textContent = 'One moment…';

    try {
      await resetCart();
      await post('/cart/add.js', { id: variantId, quantity: 1 });
      await post('/cart/update.js', { discount: '' });
      writeReturnState({ t: Date.now(), qty: null, plan: null });
      global.location.href = '/checkout';
      return;
    } catch (err) {
      console.error('[aminos-nutrition-support-checkout] One-time checkout failed', err);
    }

    resetOtpLink(link);
  }

  function resetOtpLink(link) {
    link.removeAttribute('aria-busy');
    if (link.dataset.originalLabel != null) link.textContent = link.dataset.originalLabel;
  }

  // The success path deliberately leaves the button disabled through the
  // redirect, so a double-tap during navigation can't fire a second checkout.
  // Undoing it is therefore the job of whoever brings the page back.
  function resetSubmitButton(btn) {
    if (!btn) return;
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    var label = btn.querySelector('span');
    if (label && label.dataset.originalLabel != null) label.textContent = label.dataset.originalLabel;
  }

  /* --- returning from checkout ---------------------------------------------
     The hand-off above navigates to /discount/{code}?redirect=/checkout. That
     is a SERVER redirect, so browser-Back from checkout is a fresh document
     load, not a bfcache restore: the page opens at scroll 0 with the form back
     at its markup defaults. A shopper who goes back specifically to change
     quantity therefore lands at the top of the page and has to scroll down and
     re-pick everything.

     So we record the selection on the way out and put it back on the way in.

     sessionStorage rather than localStorage: the entry must die with the tab so
     it can never fire on an unrelated later visit to this page. */
  var RETURN_KEY = 'aminosNutritionSupportReturn';
  var RETURN_MAX_AGE = 30 * 60 * 1000; // a tab left open overnight shouldn't jump
  var BUYBOX_SELECTOR = '#buybox'; // section.settings.anchor_id on this template

  function writeReturnState(state) {
    try {
      sessionStorage.setItem(RETURN_KEY, JSON.stringify(state));
    } catch (err) {
      // Safari private mode and blocked-cookie settings throw on access. Losing
      // the restore is acceptable; breaking the checkout hand-off is not.
    }
  }

  // One-shot: removed as soon as it is read, so a later normal visit to the page
  // — or a Forward navigation — doesn't re-trigger the jump.
  function readReturnState() {
    try {
      var raw = sessionStorage.getItem(RETURN_KEY);
      if (!raw) return null;
      sessionStorage.removeItem(RETURN_KEY);
      var state = JSON.parse(raw);
      if (!state || Date.now() - state.t > RETURN_MAX_AGE) return null;
      return state;
    } catch (err) {
      return null;
    }
  }

  // Mirrors the submit handler's rule: a hidden pane is not the shopper's
  // intent, so only the visible form is worth restoring into.
  function visibleForm() {
    var forms = document.querySelectorAll('[data-aminos-fp-form]');
    for (var i = 0; i < forms.length; i++) {
      var pane = forms[i].closest('[data-aminos-fp-pane]');
      if (!pane || !pane.hidden) return forms[i];
    }
    return null;
  }

  function selectRadio(el) {
    if (!el) return;
    if (!el.checked) el.checked = true;
    // Always dispatch. After Back from checkout the native `checked` can already
    // be true while `.is-selected` still sits on the other card — skipping the
    // event left the UI frozen until a full reload. The shared controller
    // (assets/aminos-featured-product.js) recomputes the selling-plan input,
    // prices and card styling from this change event.
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // The option cards hide the native radio (width/height 0 + pointer-events:
  // none) and rely on <label> to flip it. After a bfcache restore — the OTP
  // path goes straight to /checkout, so Back is persisted — WebKit/Chrome drop
  // that label→control link until reload. Clicking the card then does nothing.
  // Drive the radio ourselves so the cards work without a refresh.
  // Set once the shopper picks anything, so a slow page's restore (below) never
  // overrides a choice they have already made.
  var pickedByShopper = false;

  function onOptionClick(event) {
    var option = event.target.closest('[data-aminos-fp-option]');
    if (option || event.target.closest('.aminos-featured-product__qty-option')) pickedByShopper = true;
    if (option) {
      selectRadio(option.querySelector('[data-aminos-fp-radio]'));
      return;
    }

    var qtyOption = event.target.closest('.aminos-featured-product__qty-option');
    if (qtyOption) selectRadio(qtyOption.querySelector('[data-aminos-fp-qty]'));
  }

  function syncFormRadios(form) {
    var plan = form.querySelector('[data-aminos-fp-radio]:checked');
    if (plan) selectRadio(plan);
    var qty = form.querySelector('[data-aminos-fp-qty]:checked');
    if (qty) selectRadio(qty);
  }

  function thawForms() {
    document.querySelectorAll('[data-aminos-fp-form]').forEach(function (form) {
      form.querySelectorAll('[data-aminos-fp-radio], [data-aminos-fp-qty]').forEach(function (radio) {
        radio.disabled = false;
      });
      syncFormRadios(form);
    });
  }

  function restoreSelections(state) {
    var form = visibleForm();
    if (!form) return;

    // '' is the one-time option, which carries an empty data-selling-plan-id.
    // null = the one-time text link: nothing to re-pick, just come back to the box.
    if (state.plan === null) return;
    var wanted = String(state.plan);
    var radios = form.querySelectorAll('[data-aminos-fp-radio]');
    for (var i = 0; i < radios.length; i++) {
      if ((radios[i].dataset.sellingPlanId || '') === wanted) {
        selectRadio(radios[i]);
        break;
      }
    }

    if (!state.qty) return;
    var quantities = form.querySelectorAll('[data-aminos-fp-qty]');
    for (var j = 0; j < quantities.length; j++) {
      if (quantities[j].value === String(state.qty)) {
        selectRadio(quantities[j]);
        break;
      }
    }
  }

  function buyboxTarget() {
    var anchor = document.querySelector(BUYBOX_SELECTOR);
    if (!anchor) return null; // anchor_id blanked in the theme editor
    // anchor_id renders a zero-height <span> at the section's top edge; scroll
    // the section so the offer actually fills the viewport.
    return (anchor.closest && anchor.closest('[data-aminos-fp-section]')) || anchor;
  }

  function jumpToBuybox(target) {
    // behavior:'auto' on purpose. This is a restore, not a user-initiated jump —
    // smooth-scrolling the page by itself on load reads as a glitch.
    target.scrollIntoView({ behavior: 'auto', block: 'start' });
  }

  function scrollToBuybox() {
    var target = buyboxTarget();
    if (!target) return;

    // Stop the browser's own restoration racing ours; put it back afterwards so
    // ordinary navigation on this page is unaffected.
    var previousRestoration = null;
    try {
      if ('scrollRestoration' in history) {
        previousRestoration = history.scrollRestoration;
        history.scrollRestoration = 'manual';
      }
    } catch (err) {
      previousRestoration = null;
    }

    function releaseRestoration() {
      try {
        if (previousRestoration !== null) history.scrollRestoration = previousRestoration;
      } catch (err) {
        // no-op
      }
    }

    // The page keeps growing after pageshow — lazy images resolve and the
    // testimonials carousel adds is-enhanced — so a single scrollIntoView lands
    // short. Re-assert until the target's offset stops moving.
    var frames = 0;
    var lastTop = null;
    var stable = 0;

    function step() {
      jumpToBuybox(target);
      var top = Math.round(target.getBoundingClientRect().top);
      stable = lastTop !== null && top === lastTop ? stable + 1 : 0;
      lastTop = top;
      frames++;

      if (stable < 3 && frames < 40) {
        global.requestAnimationFrame(step);
        return;
      }
      releaseRestoration();
    }

    global.requestAnimationFrame(step);

    // Images that finish after the rAF budget can still shift the page under us.
    if (document.readyState !== 'complete') {
      global.addEventListener(
        'load',
        function () {
          var current = buyboxTarget();
          if (current) jumpToBuybox(current);
        },
        { once: true }
      );
    }
  }

  // Back from checkout restores this page from the back/forward cache, DOM and
  // all — including the disabled, "Adding…" button we left behind, which left
  // the offer impossible to re-submit. pageshow fires on bfcache restore as
  // well as first load. Always thaw the radios: a persisted restore keeps the
  // values but the label→radio link is often dead until reload.
  global.addEventListener('pageshow', function (event) {
    document.querySelectorAll('[data-aminos-fp-form] [data-aminos-fp-submit]').forEach(resetSubmitButton);
    document.querySelectorAll('[data-ns-otp-checkout]').forEach(resetOtpLink);
    thawForms();

    // A bfcache restore already carries the right scroll offset and the radios
    // still hold what the shopper picked, so there is nothing to put back — and
    // re-applying would clobber any change they made after coming back.
    if (event && event.persisted) return;

    var state = readReturnState();
    if (!state) return;

    if (!pickedByShopper) restoreSelections(state);
    scrollToBuybox();
  });

  document.addEventListener('click', onOptionClick);

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-ns-otp-checkout]');
    if (!link) return;
    event.preventDefault();
    checkoutOneTime(link);
  });

  document.addEventListener(
    'submit',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var form = target.closest('[data-aminos-fp-form]');
      if (!form) return;

      // If this pane is hidden (chip switched away from it), don't add the
      // wrong product. Only the visible form is the user's intent.
      var pane = form.closest('[data-aminos-fp-pane]');
      if (pane && pane.hidden) return;

      event.preventDefault();
      event.stopPropagation(); // keeps the shared bubble-phase handler from also adding

      checkout(form);
    },
    true
  );
})(window);
